import { api } from '../../shared/api/client'

/** Параметры GET-запроса списка карточек кабинета Ozon */
export interface OzProductsQueryParams {
  /** CSV значений для отбора по списку артикулов: сопоставляется по offer_id, sku, product_id, pim_article, barcode */
  articles?: string
  /**
   * Режим проверки списка артикулов: '1' | 'true'. Прочие фильтры (кроме
   * продавца) игнорируются, в ответе только `articles_matched`.
   */
  articles_resolve?: string
  [key: string]: any
}

/** Данные ответа списка карточек Ozon */
export interface OzProductsResponseData {
  db_products?: any[]
  products?: any[]
  total?: number
  totalIsApproximate?: boolean
  nextCursor?: string | null
  /** Только в режиме articles_resolve — подмножество переданных значений, которым нашлась карточка */
  articles_matched?: string[]
  [key: string]: any
}

/** Новые габариты одной карточки: мм и граммы; не заданное поле не меняется. */
export interface OzDimensionChange {
  offer_id: string
  depth?: number
  width?: number
  height?: number
  weight?: number
}

export const ozonAPI = {
  getSellers: () => api.get('/sellers', { params: { marketplace: 'ozon' } }),
  getSeller: (sellerId: number) => api.get(`/sellers/${sellerId}`),
  getProducts: (sellerId: number, params?: OzProductsQueryParams, signal?: AbortSignal) =>
    api.get(`/ozon/sellers/${sellerId}/products`, { params, signal, timeout: 120000 }),
  /**
   * Проверка списка артикулов на сервере: тот же запрос списка карточек с
   * articles_resolve=1 — сервер игнорирует прочие фильтры и возвращает
   * подмножество переданных значений, для которых нашлась карточка.
   */
  resolveArticles: async (sellerId: number, values: string[]): Promise<string[]> => {
    const response = await api.get(`/ozon/sellers/${sellerId}/products`, {
      params: { articles: values.join(','), articles_resolve: '1', limit: 1 },
      timeout: 60000,
    })
    const data = response.data?.data ?? response.data
    return Array.isArray(data?.articles_matched) ? data.articles_matched : []
  },
  /** Полная карточка с происхождением, SEO и словарём подписей атрибутов. */
  getProductByOfferId: (sellerId: number, offerId: string, signal?: AbortSignal) =>
    api.get(`/ozon/sellers/${sellerId}/products/${encodeURIComponent(offerId)}`, { signal, timeout: 60000 }),
  refreshProductCard: (sellerId: number, offerId: string) =>
    api.post(`/ozon/sellers/${sellerId}/products/${encodeURIComponent(offerId)}/refresh`, {}, { timeout: 120000 }),
  getProductStatusStats: (sellerId: number) => api.get(`/ozon/sellers/${sellerId}/products/stats`),
  /** Фасетные счётчики под текущие фильтры каталога */
  getCatalogFacets: (sellerId: number, params?: any, signal?: AbortSignal) =>
    api.get(`/ozon/sellers/${sellerId}/products/facets`, { params, signal, timeout: 120000 }),
  /** Бренды и категории кабинета для выпадающих фильтров */
  getCatalogFilterOptions: (sellerId: number) =>
    api.get(`/ozon/sellers/${sellerId}/products/filter-options`, { timeout: 60000 }),
  getLastSyncInfo: (sellerId: number) => api.get(`/ozon/sellers/${sellerId}/last-sync`),
  createProduct: (sellerId: number, items: any[]) =>
    api.post(`/ozon/sellers/${sellerId}/products`, { items }),
  updateProduct: (sellerId: number, body?: { offerIds?: string[]; productIds?: number[]; selectedFields?: string[]; jobId?: string }) =>
    api.post(`/ozon/sellers/${sellerId}/products/update`, body || {}, { timeout: 1800000 }),
  getUpdateFields: () => api.get('/ozon/update-fields'),
  /** Массовая правка габаритов: было → станет по свежим данным Ozon, без отправки */
  previewDimensions: (sellerId: number, items: OzDimensionChange[]) =>
    api.post(`/ozon/sellers/${sellerId}/dimensions/preview`, { items }, { timeout: 120000 }),
  /** Отправка габаритов фоном: ответ — { jobId }, итог — getDimensionsJob */
  applyDimensions: (sellerId: number, items: OzDimensionChange[]) =>
    api.post(`/ozon/sellers/${sellerId}/dimensions/apply`, { items }),
  getDimensionsJob: (sellerId: number, jobId: string) =>
    api.get(`/ozon/sellers/${sellerId}/dimensions/jobs/${encodeURIComponent(jobId)}`),
  /** Одна страница (до 1000 карточек) */
  syncProducts: (sellerId: number, jobId?: string) =>
    api.post(`/ozon/sellers/${sellerId}/sync/products`, { jobId }, { timeout: 1800000 }),
  /** Весь кабинет постранично; includeArchived догружает архив отдельным проходом */
  syncAllProducts: (sellerId: number, jobId?: string, includeArchived = false) =>
    api.post(`/ozon/sellers/${sellerId}/sync/products/full`, { jobId, includeArchived }, { timeout: 1800000 }),
  getOrders: (sellerId: number, params?: any) => api.get(`/ozon/sellers/${sellerId}/orders`, { params }),
  getAnalytics: (sellerId: number, params?: any) => api.get(`/ozon/sellers/${sellerId}/analytics`, { params }),
  getStock: (sellerId: number, params?: any) => api.get(`/ozon/sellers/${sellerId}/stock`, { params }),
  getPrices: (sellerId: number, params?: any) => api.get(`/ozon/sellers/${sellerId}/prices`, { params }),
  updatePrices: (sellerId: number, prices: any[]) => api.post(`/ozon/sellers/${sellerId}/prices`, { prices }),
  updateStock: (sellerId: number, stock: any[]) => api.post(`/ozon/sellers/${sellerId}/stock`, { stock }),
  getWarehouses: (sellerId: number) => api.get(`/ozon/sellers/${sellerId}/warehouses`),
  // Linking
  getLinkingStats: (sellerId: number) =>
    api.get(`/ozon/sellers/${sellerId}/linking-stats`, { timeout: 15000 }),
  smartAutoLink: (sellerId: number) =>
    api.post(`/ozon/sellers/${sellerId}/smart-auto-link`, {}, { timeout: 300000 }),
  /** Связывание с PIM отдельной задачей; passes — сколько проходов подряд. */
  autoLinkProducts: (sellerId: number, passes = 1, jobId?: string) =>
    api.post(`/ozon/sellers/${sellerId}/sync/auto-link`, { passes, jobId }, { timeout: 1800000 }),
  getPotentialMatches: (sellerId: number, params?: { limit?: number; offset?: number }) =>
    api.get(`/ozon/sellers/${sellerId}/potential-matches`, { params: params || {}, timeout: 120000 }),
  linkProduct: (sellerId: number, pimArticle: string, ozonProductId: number) =>
    api.post(`/ozon/sellers/${sellerId}/link-product`, { pimArticle, ozonProductId }),
  getUnlinkedProducts: (sellerId: number, params?: { search?: string; limit?: number; offset?: number }) =>
    api.get(`/ozon/sellers/${sellerId}/unlinked-products`, { params: params || {}, timeout: 60000 }),
}
